import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, ChevronRight, ChevronLeft, Loader2 } from 'lucide-react';
import {
  useVerifyApplicationToken,
  useUpdateApplicationStep,
  useSubmitApplication,
} from '../hooks/useTenantApplications';

// Extract {{variable_name}} patterns from template HTML, preserving insertion order
function extractTemplateVars(html: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const m of html.matchAll(/\{\{(\w+)\}\}/g)) {
    if (!seen.has(m[1])) {
      seen.add(m[1]);
      result.push(m[1]);
    }
  }
  return result;
}

function varLabel(v: string) {
  return v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function inputType(v: string) {
  if (v.includes('email')) return 'email';
  if (v.includes('phone')) return 'tel';
  if (v.includes('date') || v.includes('dob') || v.includes('birth')) return 'date';
  return 'text';
}

type Step = 'personal' | 'template' | 'guarantor' | 'consent' | 'review';

const STEP_LABELS: Record<Step, string> = {
  personal: 'Personal Info',
  template: 'Application Form',
  guarantor: 'Guarantor',
  consent: 'NDPR Consent',
  review: 'Submit',
};

export default function ApplyKyc() {
  const { token = '' } = useParams<{ token: string }>();

  const { data: application, isLoading, error } = useVerifyApplicationToken(token);
  const updateStep = useUpdateApplicationStep(token);
  const submitApplication = useSubmitApplication(token);

  // Derive step list based on whether a template is present
  const hasTemplate = !!(application as any)?.kycTemplate?.bodyHtml;
  const templateVars = hasTemplate
    ? extractTemplateVars((application as any).kycTemplate.bodyHtml).filter(
        (v) => !['first_name', 'last_name'].includes(v)
      )
    : [];
  const STEPS: Step[] = hasTemplate
    ? ['personal', 'template', 'consent', 'review']
    : ['personal', 'guarantor', 'consent', 'review'];

  const [currentStep, setCurrentStep] = useState<Step>('personal');
  const [submitted, setSubmitted] = useState(false);

  // Form state, personal
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Form state, template-driven
  const [formData, setFormData] = useState<Record<string, string>>({});
  // Form state, hardcoded guarantor (fallback when no template)
  const [guarantorName, setGuarantorName] = useState('');
  const [guarantorEmail, setGuarantorEmail] = useState('');
  const [guarantorPhone, setGuarantorPhone] = useState('');

  const [ndprConsent, setNdprConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Pre-fill from existing application data
  useEffect(() => {
    if (!application) return;
    if (application.firstName) setFirstName(application.firstName);
    if (application.lastName) setLastName(application.lastName);
    if ((application as any).formData) setFormData((application as any).formData);
  }, [application]);

  // ─── Loading / error states ───────────────────────────────────────────────

  if (isLoading) {
    return (
      <div role="status" className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" aria-label="Loading" />
      </div>
    );
  }

  if (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 410) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '';
      const isApproved = msg.toLowerCase().includes('approved');
      return (
        <StatusScreen
          icon={
            isApproved ? (
              <CheckCircle className="w-16 h-16 text-green-500" />
            ) : (
              <Clock className="w-16 h-16 text-yellow-500" />
            )
          }
          title={isApproved ? 'Application Already Approved' : 'Link Expired'}
          message={
            isApproved
              ? 'Your application has been approved. Please check your email for your account invitation.'
              : 'This application link has expired. Please contact your property manager for a new invite.'
          }
        />
      );
    }
    return (
      <StatusScreen
        icon={<XCircle className="w-16 h-16 text-red-500" />}
        title="Link Not Found"
        message="This application link is invalid or has already been used. Please contact your property manager."
      />
    );
  }

  if (!application) return null;

  // Returning tenant, expired lease: skip KYC wizard, update existing profile instead
  if ((application as any).skipOnboarding) {
    return (
      <StatusScreen
        icon={<CheckCircle className="w-16 h-16 text-primary-500" />}
        title="Welcome Back"
        message="Your profile is already on file. Log in to your account and update your details from the tenant portal if needed. Contact your property manager once you're ready to sign a new lease."
      />
    );
  }

  if (application.status === 'SUBMITTED' || submitted) {
    return (
      <StatusScreen
        icon={<CheckCircle className="w-16 h-16 text-green-500" />}
        title="Application Submitted"
        message="Thank you! Your application has been submitted for review. You will be contacted once a decision has been made."
      />
    );
  }

  if (application.status === 'APPROVED') {
    return (
      <StatusScreen
        icon={<CheckCircle className="w-16 h-16 text-green-500" />}
        title="Application Approved"
        message="Your application has been approved. Please check your email for your account invitation."
      />
    );
  }

  if (application.status === 'REJECTED') {
    return (
      <StatusScreen
        icon={<XCircle className="w-16 h-16 text-red-500" />}
        title="Application Unsuccessful"
        message={
          application.rejectionReason
            ? `Unfortunately your application was not successful. Reason: ${application.rejectionReason}`
            : 'Unfortunately your application was not successful. Please contact your property manager for more information.'
        }
      />
    );
  }

  // ─── Step navigation ──────────────────────────────────────────────────────

  const stepIndex = STEPS.indexOf(currentStep);

  const saveAndNext = async (payload: Parameters<typeof updateStep.mutateAsync>[0]) => {
    setSaving(true);
    setSaveError('');
    try {
      await updateStep.mutateAsync(payload);
      setCurrentStep(STEPS[stepIndex + 1]);
    } catch {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await updateStep.mutateAsync({ ndprConsent: true });
      await submitApplication.mutateAsync();
      setSubmitted(true);
    } catch {
      setSaveError('Failed to submit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => setCurrentStep(STEPS[stepIndex - 1]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-primary-600 rounded-xl mx-auto mb-3 flex items-center justify-center">
            <span className="text-white font-bold text-lg">O</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Tenancy Application</h1>
          <p className="text-gray-500 text-sm mt-1">
            Complete all steps to submit your application
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
          {STEPS.map((step, i) => (
            <div key={step} className="flex items-center gap-2">
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    i < stepIndex
                      ? 'bg-primary-600 text-white'
                      : i === stepIndex
                        ? 'bg-primary-100 text-primary-700 border-2 border-primary-600'
                        : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {i < stepIndex ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                <span className="text-xs text-gray-500 mt-1">{STEP_LABELS[step]}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`h-0.5 w-6 mb-4 ${i < stepIndex ? 'bg-primary-600' : 'bg-gray-200'}`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">{STEP_LABELS[currentStep]}</h2>
          {currentStep === 'template' && (application as any)?.kycTemplate?.name && (
            <p className="text-xs text-gray-400 mb-4">{(application as any).kycTemplate.name}</p>
          )}

          {saveError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {saveError}
            </div>
          )}

          {/* ── Personal Info ─────────────────────────────────────────────── */}
          {currentStep === 'personal' && (
            <div className="space-y-4">
              {[
                {
                  label: 'First Name',
                  value: firstName,
                  setter: setFirstName,
                  placeholder: 'Enter your first name',
                },
                {
                  label: 'Last Name',
                  value: lastName,
                  setter: setLastName,
                  placeholder: 'Enter your last name',
                },
              ].map(({ label, value, setter, placeholder }) => (
                <div key={label}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder={placeholder}
                  />
                </div>
              ))}
              <button
                onClick={() =>
                  saveAndNext({
                    firstName: firstName || undefined,
                    lastName: lastName || undefined,
                  })
                }
                disabled={saving || !firstName.trim() || !lastName.trim()}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* ── Template-driven fields ────────────────────────────────────── */}
          {currentStep === 'template' && (
            <div className="space-y-4">
              {templateVars.length === 0 && (
                <p className="text-sm text-gray-500 italic">
                  No additional fields required by this template.
                </p>
              )}
              {templateVars.map((varName) => (
                <div key={varName}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {varLabel(varName)}
                  </label>
                  <input
                    type={inputType(varName)}
                    value={formData[varName] ?? ''}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, [varName]: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder={varLabel(varName)}
                  />
                </div>
              ))}
              <div className="flex gap-3">
                <button
                  onClick={goBack}
                  className="flex-1 flex items-center justify-center gap-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={() => saveAndNext({ formData })}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Hardcoded Guarantor (no-template fallback) ────────────────── */}
          {currentStep === 'guarantor' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Please provide details of someone who can act as your guarantor.
              </p>
              {[
                {
                  label: 'Guarantor Full Name',
                  value: guarantorName,
                  setter: setGuarantorName,
                  type: 'text',
                  placeholder: 'Full name',
                },
                {
                  label: 'Guarantor Email',
                  value: guarantorEmail,
                  setter: setGuarantorEmail,
                  type: 'email',
                  placeholder: 'email@example.com',
                },
                {
                  label: 'Guarantor Phone',
                  value: guarantorPhone,
                  setter: setGuarantorPhone,
                  type: 'tel',
                  placeholder: '+234...',
                },
              ].map(({ label, value, setter, type, placeholder }) => (
                <div key={label}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type={type}
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder={placeholder}
                  />
                </div>
              ))}
              <div className="flex gap-3">
                <button
                  onClick={goBack}
                  className="flex-1 flex items-center justify-center gap-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={() =>
                    saveAndNext({
                      guarantorName: guarantorName || undefined,
                      guarantorEmail: guarantorEmail || undefined,
                      guarantorPhone: guarantorPhone || undefined,
                    })
                  }
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── NDPR Consent ──────────────────────────────────────────────── */}
          {currentStep === 'consent' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600 leading-relaxed max-h-48 overflow-y-auto">
                <p className="font-medium text-gray-800 mb-2">Data Protection Consent (NDPR)</p>
                <p>
                  In accordance with the Nigeria Data Protection Regulation (NDPR), we are
                  collecting your personal data for the purpose of processing your tenancy
                  application. Your data will be used solely for this purpose and will not be shared
                  with third parties without your consent. You have the right to access, correct, or
                  request deletion of your data at any time by contacting your property manager.
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ndprConsent}
                  onChange={(e) => setNdprConsent(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  I consent to the collection and processing of my personal data as described above.
                </span>
              </label>
              <div className="flex gap-3">
                <button
                  onClick={goBack}
                  className="flex-1 flex items-center justify-center gap-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={() => {
                    if (!ndprConsent) {
                      setSaveError('You must give consent to proceed.');
                      return;
                    }
                    setSaveError('');
                    setCurrentStep('review');
                  }}
                  className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 rounded-lg font-medium hover:bg-primary-700"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Review & Submit ───────────────────────────────────────────── */}
          {currentStep === 'review' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Please review your information before submitting. Once submitted, your application
                will be reviewed by your property manager.
              </p>
              <div className="border border-gray-100 rounded-lg divide-y divide-gray-100 text-sm">
                <ReviewRow label="First Name" value={firstName || application.firstName} />
                <ReviewRow label="Last Name" value={lastName || application.lastName} />
                {hasTemplate ? (
                  templateVars.map((v) => (
                    <ReviewRow key={v} label={varLabel(v)} value={formData[v]} />
                  ))
                ) : (
                  <>
                    <ReviewRow label="Guarantor" value={guarantorName} />
                    <ReviewRow label="Guarantor Email" value={guarantorEmail} />
                    <ReviewRow label="Guarantor Phone" value={guarantorPhone} />
                  </>
                )}
                <ReviewRow label="NDPR Consent" value={ndprConsent ? 'Given' : 'Not given'} />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={goBack}
                  className="flex-1 flex items-center justify-center gap-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving || !ndprConsent}
                  className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Submit Application
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusScreen({
  icon,
  title,
  message,
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        <div className="flex justify-center mb-4">{icon}</div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">{title}</h1>
        <p className="text-gray-500 text-sm leading-relaxed">{message}</p>
      </div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between px-4 py-2.5">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium">{value || 'N/A'}</span>
    </div>
  );
}
